import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import CardMembershipRoundedIcon from '@mui/icons-material/CardMembershipRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import QueueRoundedIcon from '@mui/icons-material/QueueRounded';
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import ApiRoundedIcon from '@mui/icons-material/ApiRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { colors } from '../../theme';

export const DRAWER_WIDTH = 236;

const TOP_ITEMS = [{ label: 'Dashboard', path: '/platform', icon: <DashboardRoundedIcon />, end: true }];

const NAV_GROUPS = [
  {
    label: 'Billing',
    icon: <WorkspacePremiumRoundedIcon />,
    items: [
      { label: 'Companies', path: '/platform/companies', icon: <BusinessRoundedIcon /> },
      { label: 'Users', path: '/platform/users', icon: <GroupRoundedIcon /> },
      { label: 'Subscriptions', path: '/platform/subscriptions', icon: <WorkspacePremiumRoundedIcon /> },
      { label: 'Plans', path: '/platform/plans', icon: <CardMembershipRoundedIcon /> },
      { label: 'Coupons', path: '/platform/coupons', icon: <LocalOfferRoundedIcon /> },
      { label: 'Invoices', path: '/platform/invoices', icon: <ReceiptLongRoundedIcon /> },
      { label: 'Payments', path: '/platform/payments', icon: <PaymentsRoundedIcon /> },
      { label: 'Wallet Ledger', path: '/platform/wallet-ledger', icon: <AccountBalanceWalletRoundedIcon /> },
      { label: 'Revenue', path: '/platform/revenue', icon: <TrendingUpRoundedIcon /> },
      { label: 'Support Tickets', path: '/platform/support-tickets', icon: <SupportAgentRoundedIcon /> },
    ],
  },
  {
    label: 'Configuration',
    icon: <TuneRoundedIcon />,
    items: [
      { label: 'Global Settings', path: '/platform/settings', icon: <TuneRoundedIcon /> },
      { label: 'SMTP', path: '/platform/settings/smtp', icon: <MarkEmailReadRoundedIcon /> },
      { label: 'Storage', path: '/platform/settings/storage', icon: <StorageRoundedIcon /> },
      { label: 'Feature Flags', path: '/platform/feature-flags', icon: <FlagRoundedIcon /> },
      { label: 'Maintenance Mode', path: '/platform/settings/maintenance', icon: <BuildRoundedIcon /> },
    ],
  },
  {
    label: 'Monitoring',
    icon: <MonitorHeartRoundedIcon />,
    items: [
      { label: 'Redis Monitor', path: '/platform/monitor/redis', icon: <MemoryRoundedIcon /> },
      { label: 'Queue Monitor', path: '/platform/monitor/queues', icon: <QueueRoundedIcon /> },
      { label: 'Logs', path: '/platform/monitor/logs', icon: <ArticleRoundedIcon /> },
      { label: 'API Monitor', path: '/platform/monitor/api', icon: <ApiRoundedIcon /> },
      { label: 'System Health', path: '/platform/monitor/health', icon: <MonitorHeartRoundedIcon /> },
    ],
  },
];

const navItemSx = {
  color: colors.slate[300],
  py: 0.65,
  minHeight: 34,
  '& .MuiListItemIcon-root': { color: colors.slate[500], minWidth: 32, '& svg': { fontSize: 19 } },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
  '&.active': {
    bgcolor: colors.amber[600],
    color: '#fff',
    boxShadow: `0 4px 12px -2px ${colors.amber[600]}80`,
    '& .MuiListItemIcon-root': { color: '#fff' },
    '&:hover': { bgcolor: colors.amber[600] },
  },
};

const childItemSx = {
  ...navItemSx,
  py: 0.5,
  minHeight: 30,
  pl: 1.5,
  '& .MuiListItemIcon-root': { color: colors.slate[500], minWidth: 28, '& svg': { fontSize: 17 } },
};

const groupHeaderSx = {
  color: colors.slate[300],
  py: 0.65,
  minHeight: 34,
  '& .MuiListItemIcon-root': { color: colors.slate[500], minWidth: 32, '& svg': { fontSize: 19 } },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
};

function NavGroup({ group, isActive }) {
  const [open, setOpen] = useState(isActive);

  return (
    <>
      <ListItemButton onClick={() => setOpen((v) => !v)} sx={groupHeaderSx}>
        <ListItemIcon>{group.icon}</ListItemIcon>
        <ListItemText primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }} primary={group.label} />
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 18,
            color: colors.slate[600],
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.25 }}>
          {group.items.map((item) => (
            <ListItemButton key={item.path} component={NavLink} to={item.path} end={item.end} sx={childItemSx}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 12 }} primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
}

export default function PlatformSidebar() {
  const location = useLocation();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          bgcolor: colors.slate[950],
          color: colors.slate[300],
          border: 'none',
        },
      }}
    >
      <Box sx={{ px: 2.25, py: 2.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '9px',
              background: `linear-gradient(135deg, ${colors.amber[500]}, ${colors.amber[600]})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px -2px ${colors.amber[600]}66`,
            }}
          >
            <AdminPanelSettingsRoundedIcon sx={{ color: '#fff', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
              Platform Admin
            </Typography>
            <Typography sx={{ color: colors.slate[500], fontSize: 10, fontWeight: 500 }}>
              ClientConnect
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box
        sx={{
          px: 1.5,
          flexGrow: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
          {TOP_ITEMS.map((item) => (
            <ListItemButton key={item.path} component={NavLink} to={item.path} end={item.end} sx={navItemSx}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }} primary={item.label} />
            </ListItemButton>
          ))}
        </List>

        <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {NAV_GROUPS.map((group) => (
            <NavGroup
              key={group.label}
              group={group}
              isActive={group.items.some((item) => location.pathname.startsWith(item.path))}
            />
          ))}
        </List>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${colors.slate[900]}` }}>
        <ListItemButton component={NavLink} to="/" sx={navItemSx}>
          <ListItemIcon>
            <ArrowBackRoundedIcon />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: 12.5, fontWeight: 600 }} primary="Back to App" />
        </ListItemButton>
      </Box>

      <Box sx={{ py: 1.25, textAlign: 'center', borderTop: `1px solid ${colors.slate[900]}` }}>
        <Typography sx={{ color: colors.slate[700], fontSize: 10, fontWeight: 500 }}>
          Powered by ClientConnect
        </Typography>
      </Box>
    </Drawer>
  );
}
